import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Avatar,
  Rating,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ShoppingBag as ProductIcon,
  Schedule as PendingIcon,
  CheckCircleOutline as ApprovedIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import api from '../services/api';
import { toast } from 'react-toastify';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [filters, setFilters] = useState({
    rating: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0
  });

  const ratingOptions = [
    { value: '', label: 'All Ratings' },
    { value: '5', label: '5 Stars' },
    { value: '4', label: '4 Stars' },
    { value: '3', label: '3 Stars' },
    { value: '2', label: '2 Stars' },
    { value: '1', label: '1 Star' }
  ];

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page + 1,
        limit: pagination.pageSize,
        rating: filters.rating,
        search: filters.search
      };

      const response = await api.get('/reviews', { params });
      setReviews(response.data.reviews);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [fetchReviews]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/reviews/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch review stats:', error);
    }
  };

  const handleRespondToReview = async () => {
    try {
      await api.post(`/reviews/${selectedReview._id}/respond`, {
        text: responseText
      });
      toast.success('Response added successfully');
      setRespondDialogOpen(false);
      setResponseText('');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to respond to review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      try {
        await api.delete(`/reviews/${reviewId}`);
        toast.success('Review deleted successfully');
        fetchReviews();
        fetchStats();
      } catch (error) {
        toast.error('Failed to delete review');
      }
    }
  };

  const handleViewReview = (review) => {
    setSelectedReview(review);
    setViewDialogOpen(true);
  };

  const columns = [
    { field: '_id', headerName: 'ID', width: 220 },
    {
      field: 'productId',
      headerName: 'Product',
      width: 200,
      renderCell: (params) => params.row.productId?.title || params.row.productId?.name || 'Unknown Product'
    },
    {
      field: 'userName',
      headerName: 'User',
      width: 160,
      renderCell: (params) => (
        <div className="flex flex-col">
          <span>{params.row.userName || 'Anonymous'}</span>
          <span className="text-xs text-gray-500">{params.row.userEmail}</span>
        </div>
      )
    },
    {
      field: 'rating',
      headerName: 'Rating',
      width: 120,
      renderCell: (params) => (
        <div className="flex items-center gap-1">
          <span>{params.row.rating}</span>
          <span className="text-xs text-gray-500">/5</span>
        </div>
      )
    },
    {
      field: 'createdAt',
      headerName: 'Submitted',
      width: 160,
      valueGetter: (params) => new Date(params.value).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        dateStyle: "short",
        timeStyle: "short"
      })
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      renderCell: (params) => (
        <div className="flex gap-2">
          <Button variant="outlined" size="small" onClick={() => handleViewReview(params.row)}>
            View
          </Button>
          <IconButton color="error" onClick={() => handleDeleteReview(params.row._id)}>
            <DeleteIcon />
          </IconButton>
        </div>
      )
    }
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reviews & Ratings Management
      </Typography>

      {/* Stats Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <StarIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{stats.stats?.totalReviews || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Reviews
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PendingIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{stats.stats?.pendingReviews || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Reviews
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ApprovedIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{stats.stats?.approvedReviews || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Approved Reviews
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <StarIcon color="secondary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {stats.stats?.averageRating?.toFixed(1) || '0.0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average Rating
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Filters and Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search reviews..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Rating</InputLabel>
                <Select
                  value={filters.rating}
                  onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
                >
                  {ratingOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <CardContent>
          <DataGrid
            rows={reviews}
            columns={columns}
            loading={loading}
            pageSize={pagination.pageSize}
            page={pagination.page}
            rowCount={pagination.total}
            paginationMode="server"
            onPageChange={(newPage) => setPagination({ ...pagination, page: newPage })}
            onPageSizeChange={(newPageSize) => setPagination({ ...pagination, pageSize: newPageSize })}
            checkboxSelection={false}
            disableSelectionOnClick
            autoHeight
            getRowId={(row) => row._id}
          />
        </CardContent>
      </Card>

      {/* View Review Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Review Details</DialogTitle>
        <DialogContent>
          {selectedReview && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Customer Information</Typography>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ mr: 2 }}>
                      {selectedReview.userName?.charAt(0) || 'U'}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedReview.userName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedReview.userEmail}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Product Information</Typography>
                  <Box display="flex" alignItems="center">
                    <ProductIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <Typography variant="body1">
                      {selectedReview.productId?.title || selectedReview.productId?.name || 'Unknown Product'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Review Content</Typography>
                  <Box mb={2}>
                    <Rating
                      value={selectedReview.rating}
                      readOnly
                      icon={<StarIcon fontSize="inherit" />}
                      emptyIcon={<StarBorderIcon fontSize="inherit" />}
                    />
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      {selectedReview.title}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {selectedReview.comment}
                    </Typography>
                  </Box>
                  
                  {/* Video Review */}
                  {selectedReview.video && (
                    <Box mb={2}>
                      <Typography variant="h6" gutterBottom>Video Review</Typography>
                      <video
                        src={selectedReview.video.startsWith('http') ? selectedReview.video : `${process.env.REACT_APP_UPLOAD_URL}/${selectedReview.video}`}
                        controls
                        style={{ width: '100%', maxWidth: '500px', borderRadius: '8px' }}
                        poster={selectedReview.videoThumbnail ? (selectedReview.videoThumbnail.startsWith('http') ? selectedReview.videoThumbnail : `${process.env.REACT_APP_UPLOAD_URL}/${selectedReview.videoThumbnail}`) : undefined}
                      />
                    </Box>
                  )}
                  
                  {/* Review Images */}
                  {selectedReview.images && selectedReview.images.length > 0 && (
                    <Box mb={2}>
                      <Typography variant="h6" gutterBottom>
                        Review Images ({selectedReview.images.length})
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        {selectedReview.images.map((image, index) => (
                          <Box key={index} position="relative">
                            <img
                              src={image.startsWith('http') ? image : `${process.env.REACT_APP_UPLOAD_URL}/${image}`}
                              alt={`Review ${index + 1}`}
                              style={{ 
                                width: '120px', 
                                height: '120px', 
                                objectFit: 'cover', 
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(image.startsWith('http') ? image : `${process.env.REACT_APP_UPLOAD_URL}/${image}`, '_blank')}
                            />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                position: 'absolute', 
                                bottom: 4, 
                                right: 4, 
                                background: 'rgba(0,0,0,0.7)', 
                                color: 'white', 
                                px: 1, 
                                borderRadius: 1 
                              }}
                            >
                              {index + 1}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {selectedReview.adminResponse && (
                    <Box mt={3}>
                      <Typography variant="h6" gutterBottom>Admin Response</Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body1">
                          {selectedReview.adminResponse.text}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Responded on {new Date(selectedReview.adminResponse.respondedAt).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            dateStyle: "short",
                            timeStyle: "short"
                          })}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              setRespondDialogOpen(true);
              setViewDialogOpen(false);
            }}
            startIcon={<ReplyIcon />}
          >
            Respond
          </Button>
        </DialogActions>
      </Dialog>

      {/* Respond to Review Dialog */}
      <Dialog open={respondDialogOpen} onClose={() => setRespondDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Respond to Review</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            placeholder="Write your response to the customer..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRespondDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRespondToReview} variant="contained">
            Send Response
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reviews;
